import { summarizeLineupBalance } from './lineup-balance.js';

const activeEditors = new Set();
let navigationBound = false;
let profileSequence = 0;

function ensureLineupStyles() {
    if (document.getElementById('lineup-editor-styles')) return;
    const link = document.createElement('link');
    link.id = 'lineup-editor-styles';
    link.rel = 'stylesheet';
    // Webpack 5 emits this local stylesheet as an asset, without Sass/CSS loaders.
    link.href = new URL('../../www/assets/app/scss/components/lineupEditor.css', import.meta.url).href;
    document.head.append(link);
}

const clone = (value) => ({ ...value });
const equal = (a, b) => Object.keys(a).length === Object.keys(b).length
    && Object.keys(a).every((key) => a[key] === b[key]);
const number = (value, estimated = false) => value === null || value === undefined
    ? '—' : `${estimated ? '≈ ' : ''}${Number(value).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })}`;

export function initLineupEditors(root = document) {
    for (const editor of activeEditors) {
        if (!editor.element.isConnected) {
            editor.cancelDrag();
            activeEditors.delete(editor);
        }
    }
    if (!navigationBound) {
        navigationBound = true;
        window.addEventListener('blur', () => {
            activeEditors.forEach((editor) => editor.cancelDrag());
        });
        window.addEventListener('beforeunload', (event) => {
            if ([...activeEditors].some((editor) => editor.element.isConnected && editor.dirty())) {
                event.preventDefault();
                event.returnValue = '';
            }
        });
        document.addEventListener('click', (event) => {
            const link = event.target.closest?.('a.ajax[href]');
            if (link && [...activeEditors].some((editor) => editor.element.isConnected && editor.dirty())
                && !window.confirm('Máš neuložené změny sestavy. Opravdu chceš odejít?')) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        }, true);
    }
    const candidates = [...root.querySelectorAll('[data-lineup-editor]')];
    if (root.matches?.('[data-lineup-editor]')) candidates.unshift(root);
    if (candidates.length) ensureLineupStyles();
    candidates.forEach((element) => {
        if (element.dataset.lineupBound) return;
        element.dataset.lineupBound = '1';
        try {
            const config = JSON.parse(element.dataset.lineupConfig);
            activeEditors.add(createEditor(element, config));
        } catch (error) {
            const status = element.querySelector('[data-lineup-status]');
            if (status) status.textContent = 'Editor se nepodařilo načíst. Obnov stránku a zkontroluj sestavení JS.';
            console.error('Lineup editor initialization failed', error);
        }
    });
}

function createEditor(element, config) {
    const find = (selector) => element.querySelector(selector);
    const form = find('[data-lineup-form]');
    const players = config.players;
    const byId = new Map(players.map((player) => [String(player.event_member_id), player]));
    let assignments = clone(config.assignments);
    let saved = clone(assignments);
    let revision = config.revision;
    let report = config.report;
    let busy = false;
    let reportValid = true;
    let selectedSwap = null;
    let dragId = null;
    let pointerDrag = null;
    let dragPreview = null;
    let dragFrame = null;
    const undo = [];
    const rows = new Map();
    const dirty = () => !equal(assignments, saved);
    const editable = () => !config.closed && !busy;

    function message(text, isError = false) {
        const box = find('[data-lineup-message]');
        box.textContent = text;
        box.hidden = !text;
        box.classList.toggle('hidden', !text);
        box.setAttribute('role', isError ? 'alert' : 'status');
        box.classList.toggle('lineup-editor-error', isError);
    }

    function controls() {
        const locked = !editable();
        find('[data-lineup-generate]').disabled = locked || players.length < config.minimum;
        find('[data-lineup-swap-teams]').disabled = locked || !Object.values(assignments).some((group) => group === 1 || group === 2);
        find('[data-lineup-undo]').disabled = locked || !undo.length;
        find('[data-lineup-save]').disabled = locked || !dirty() || !reportValid
            || report.violations.length > 0 || !report.counts[1] || !report.counts[2];
        element.querySelectorAll('[data-lineup-swap], [data-lineup-handle]').forEach((node) => {
            node.disabled = locked;
        });
        rows.forEach((row, id) => row.querySelectorAll('[data-lineup-move]').forEach((button) => {
            button.disabled = locked || assignments[id] === Number(button.dataset.lineupMove);
        }));
        rows.forEach((row) => { row.draggable = false; });
        const status = find('[data-lineup-status]');
        status.textContent = config.closed ? 'Uzamčeno' : (dirty() ? 'Neuložené změny'
            : (Object.values(saved).some((group) => group > 0) ? 'Uloženo' : 'Bez uložené sestavy'));
        status.classList.toggle('lineup-editor-unsaved', !config.closed && dirty());
        find('[data-lineup-busy]').classList.toggle('hidden', !busy);
        element.setAttribute('aria-busy', busy ? 'true' : 'false');
        const swapStatus = find('[data-lineup-swap-status]');
        swapStatus.classList.toggle('hidden', selectedSwap === null);
        swapStatus.textContent = selectedSwap === null ? ''
            : `Vyměnit: ${byId.get(selectedSwap).nickname}. Vyber „Vyměnit“ u hráče druhého týmu, nebo stiskni Escape.`;
        rows.forEach((row, id) => {
            row.classList.toggle('lineup-editor-selected', id === selectedSwap);
            row.querySelector('[data-lineup-swap]').setAttribute('aria-pressed', id === selectedSwap ? 'true' : 'false');
        });
        form.elements.namedItem('assignments').value = JSON.stringify(assignments);
        form.elements.namedItem('revision').value = revision;
    }

    function metric(player, dimension) {
        return number(player.dimensions[dimension], player.estimated_dimensions?.includes(dimension)).replace('≈ ', '≈');
    }

    function buildRows() {
        const template = find('[data-lineup-player-template]');
        players.forEach((player) => {
            const id = String(player.event_member_id);
            const row = template.content.firstElementChild.cloneNode(true);
            row.dataset.lineupPlayer = id;
            row.querySelector('[data-lineup-name]').textContent = player.nickname;
            const meta = [];
            if (player.can_play_goalkeeper) meta.push(`Brankář ${number(player.goalkeeper_score, player.goalkeeper_estimated)}`);
            if (player.estimated_values) meta.push(`${player.estimated_values}× odhad`);
            row.querySelector('[data-lineup-meta]').textContent = meta.join(' · ');
            row.querySelector('[data-lineup-defence]').textContent = metric(player, 'role_defence');
            row.querySelector('[data-lineup-attack]').textContent = metric(player, 'role_attack');
            row.querySelector('[data-lineup-form]').textContent = metric(player, 'recent_form');
            row.querySelectorAll('[data-lineup-move]').forEach((button) => {
                const group = Number(button.dataset.lineupMove);
                button.setAttribute('aria-label', group ? `Přesunout ${player.nickname} do týmu ${group}` : `Vrátit ${player.nickname} mezi nezařazené`);
            });
            const toggle = row.querySelector('[data-lineup-profile-toggle]');
            const panel = row.querySelector('[data-lineup-profile-panel]');
            panel.id = `lineup-player-profile-${++profileSequence}`;
            toggle.setAttribute('aria-controls', panel.id);
            toggle.setAttribute('aria-label', `Profil hráče ${player.nickname}`);
            row.querySelector('[data-lineup-swap]').setAttribute('aria-label', `Vyměnit: ${player.nickname}`);
            row.querySelector('[data-lineup-handle]').setAttribute('aria-label', `Přetáhnout: ${player.nickname}`);
            const profile = row.querySelector('[data-lineup-profile]');
            const dimensions = [...new Set([...Object.keys(player.dimension_weights), 'role_defence', 'role_attack'])];
            dimensions.forEach((key) => {
                const line = document.createElement('div');
                line.className = 'lineup-editor-profile-row';
                const label = document.createElement('span');
                label.textContent = player.dimension_labels[key] || key;
                const score = document.createElement('span');
                score.textContent = metric(player, key);
                line.append(label, score);
                profile.append(line);
            });
            if (player.recent_form_events) {
                const line = document.createElement('p');
                line.textContent = `Forma z ${player.recent_form_events} hodnocených událostí.`;
                profile.append(line);
            }
            rows.set(id, row);
        });
    }

    function renderAssignments() {
        const focused = document.activeElement;
        rows.forEach((row, id) => {
            const group = assignments[id];
            const list = find(`[data-lineup-list="${group}"]`);
            // Reuse nodes: expanded profiles and listeners survive every move.
            if (row.parentElement !== list) list.append(row);
        });
        [0, 1, 2].forEach((group) => {
            const count = Object.values(assignments).filter((value) => value === group).length;
            find(`[data-lineup-count="${group}"]`).textContent = `${count} hráčů`;
            const drop = find(`[data-lineup-drop="${group}"]`);
            drop.textContent = config.closed ? (count ? '' : 'Bez hráčů') : (count ? 'Přesunout sem' : 'Zatím bez hráčů · přesunout sem');
        });
        if (focused && element.contains(focused) && document.activeElement !== focused) {
            focused.focus?.({ preventScroll: true });
        }
        controls();
        renderTeamBalance();
    }

    function average(group, dimension, goalkeeper = false) {
        const selected = players.filter((player) => assignments[player.event_member_id] === group
            && (!goalkeeper || player.can_play_goalkeeper));
        const known = selected.filter((player) => (goalkeeper ? player.goalkeeper_score : player.dimensions[dimension]) != null);
        if (!known.length) return { value: null, estimated: false };
        const values = known.map((player) => Number(goalkeeper ? player.goalkeeper_score : player.dimensions[dimension]));
        return {
            value: goalkeeper ? Math.max(...values) : values.reduce((a, b) => a + b, 0) / values.length,
            estimated: known.some((player) => goalkeeper ? player.goalkeeper_estimated : player.estimated_dimensions?.includes(dimension)),
        };
    }

    function tableRow(label, values) {
        const tr = document.createElement('tr');
        const first = document.createElement('th');
        first.scope = 'row';
        first.className = 'px-2 py-2 text-left font-medium';
        first.textContent = label;
        tr.append(first);
        values.forEach((value) => {
            const cell = document.createElement('td');
            cell.className = 'px-2 py-2 text-right';
            cell.textContent = value;
            tr.append(cell);
        });
        return tr;
    }

    function renderReport() {
        renderTeamBalance();
        const comparison = find('[data-lineup-comparison]');
        comparison.replaceChildren();
        const count1 = report.counts[1];
        const count2 = report.counts[2];
        comparison.append(tableRow('Počet hráčů', [count1, count2, Math.abs(count1 - count2)]));
        [['Obrana Ø', 'role_defence'], ['Útok Ø', 'role_attack'], ['Brankář', null], ['Forma Ø', 'recent_form']].forEach(([label, key]) => {
            const a = average(1, key, key === null);
            const b = average(2, key, key === null);
            comparison.append(tableRow(label, [number(a.value, a.estimated), number(b.value, b.estimated),
                a.value === null || b.value === null ? '—' : number(Math.abs(a.value - b.value), a.estimated || b.estimated)]));
        });
        find('[data-lineup-gap]').textContent = count1 && count2 ? number(report.gap) : '—';
        const warnings = find('[data-lineup-warnings]');
        warnings.replaceChildren();
        warnings.classList.toggle('hidden', !report.warnings.length);
        report.warnings.forEach((warning) => {
            const li = document.createElement('li');
            li.textContent = warning;
            warnings.append(li);
        });
        const dimensions = find('[data-lineup-dimensions]');
        dimensions.replaceChildren();
        Object.entries(report.differences).forEach(([key, dimension]) => {
            const a = average(1, key);
            const b = average(2, key);
            dimensions.append(tableRow(dimension.label, [number(players[0]?.dimension_weights[key]),
                number(a.value, a.estimated), number(b.value, b.estimated),
                a.value === null || b.value === null ? '—' : number(dimension.gap, a.estimated || b.estimated)]));
        });
    }

    function renderTeamBalance() {
        const summary = summarizeLineupBalance(players, assignments);
        const labels = { profile: 'Profil Ø', defence: 'Obrana Ø', attack: 'Útok Ø', form: 'Forma Ø', goalkeeper: 'Brankář' };
        for (const group of [1, 2]) {
            const container = find(`[data-lineup-team-metrics="${group}"]`);
            if (!container) continue;
            container.replaceChildren();
            for (const [key, label] of Object.entries(labels)) {
                const metric = summary.teams[group][key];
                const difference = summary.differences[key];
                const item = document.createElement('div');
                item.className = 'lineup-editor-team-metric';
                const caption = document.createElement('dt');
                caption.textContent = label;
                const value = document.createElement('dd');
                value.textContent = number(metric.value, metric.estimated);
                if (difference.level) {
                    const flag = document.createElement('span');
                    flag.className = `lineup-editor-difference lineup-editor-difference-${difference.level}`;
                    flag.textContent = '!'.repeat(difference.level);
                    const direction = metric.value > summary.teams[3 - group][key].value ? 'vyšší' : 'nižší';
                    flag.setAttribute('role', 'img');
                    flag.setAttribute('aria-label', `${label}: o ${number(difference.gap)} bodu ${direction} než druhý tým`);
                    value.append(flag);
                }
                item.append(caption, value);
                container.append(item);
            }
            const share = find(`[data-lineup-team-share="${group}"]`);
            if (share) share.textContent = summary.shares[group] === null ? '—' : `${summary.shares[group]} %`;
            const status = find(`[data-lineup-team-advantage="${group}"]`);
            if (status) {
                const profileLevel = Math.max(...Object.values(summary.differences).map((difference) => difference.level));
                status.className = `lineup-editor-advantage lineup-editor-difference-${summary.level}`;
                status.textContent = summary.balance === null ? 'Nedostatek dat pro porovnání'
                    : (summary.level === 0 ? (profileLevel ? `Síly podobné · ${'!'.repeat(profileLevel)} Rozdílné profily` : 'Vyrovnané síly')
                        : `${'!'.repeat(summary.level)} ${summary.shares[group] > 50 ? 'Převaha' : 'Slabší tým'}${summary.level === 2 ? ' · výrazný rozdíl' : ''}`);
                if (!summary.level && profileLevel) status.classList.add(`lineup-editor-difference-${profileLevel}`);
                if (summary.teams[group].count && !players.some((player) => assignments[player.event_member_id] === group && player.can_play_goalkeeper)) {
                    status.textContent += ' · ! Bez brankáře';
                    if (!summary.level) status.classList.add('lineup-editor-difference-1');
                }
            }
        }
        const balance = find('[data-lineup-balance-summary]');
        if (balance) {
            const notes = [];
            if (summary.unassigned) notes.push(`${summary.unassigned} nezařazených`);
            if (summary.teams[1].count !== summary.teams[2].count) notes.push('! Rozdílný počet hráčů');
            const estimate = players.some((player) => assignments[player.event_member_id] > 0 && player.estimated_values > 0);
            if (estimate) notes.push('obsahuje odhady');
            balance.textContent = `${summary.balance === null ? 'Vyrovnanost zatím nelze určit' : `Vyrovnanost celkové síly ${summary.balance} %`}${notes.length ? ` · ${notes.join(' · ')}` : ''}`;
        }
    }

    function renderRules() {
        const list = find('[data-lineup-rules]');
        const memberName = (id) => players.find((player) => player.team_member_id === id)?.nickname || 'Hráč mimo tuto účast';
        config.rules.forEach((rule) => {
            const li = document.createElement('li');
            const name = rule.member_nickname || memberName(rule.member_id);
            const related = rule.related_nickname || memberName(rule.related_member_id);
            li.textContent = rule.rule_type === 'avoid_same_team'
                ? `${name} + ${related}: každý v jiném týmu.`
                : `${name}: při lichém počtu nesmí být v menším týmu.`;
            list.append(li);
        });
        if (!config.rules.length) {
            const li = document.createElement('li');
            li.textContent = 'Žádná aktivní pravidla hráčů.';
            list.append(li);
        }
    }

    function remember() {
        undo.push(clone(assignments));
        if (undo.length > 50) undo.shift();
    }

    async function request(operation) {
        if (!editable()) return;
        const focused = document.activeElement;
        busy = true;
        controls();
        find('[data-lineup-busy]').textContent = operation === 'save' ? 'Ukládám…' : (operation === 'generate' ? 'Generuji návrh…' : 'Přepočítávám…');
        find('[data-lineup-retry]')?.classList.add('hidden');
        try {
            const data = new FormData(form);
            data.set('operation', operation);
            data.set('assignments', JSON.stringify(assignments));
            data.set('revision', revision);
            data.set(find('[data-lineup-save]').name, '1');
            const response = await window.naja.makeRequest('POST', form.action, data, { history: false });
            if (!response?.ok) throw new Error(response?.message || 'Požadavek se nepodařilo dokončit. Obnov stránku.');
            if (operation === 'generate') {
                remember();
                assignments = clone(response.assignments);
                selectedSwap = null;
            }
            if (operation === 'save') {
                saved = clone(response.assignments);
                revision = response.revision;
                undo.length = 0;
            }
            report = response.report;
            reportValid = true;
            renderAssignments();
            renderReport();
            message(response.message || '');
        } catch (error) {
            reportValid = false;
            message(error.message || 'Spojení se nezdařilo. Rozpracované změny zůstaly zachované.', true);
            find('[data-lineup-retry]')?.classList.remove('hidden');
        } finally {
            busy = false;
            controls();
            if (focused?.isConnected && element.contains(focused)
                && (!document.activeElement || document.activeElement === document.body || document.activeElement === focused)) {
                focused.focus?.({ preventScroll: true });
            }
        }
    }

    function change(id, group, targetId = null) {
        if (!editable() || !byId.has(id) || ![0, 1, 2].includes(group)) return;
        if (assignments[id] === group) return;
        remember();
        const previous = assignments[id];
        assignments[id] = group;
        if (targetId && byId.has(targetId)) assignments[targetId] = previous;
        selectedSwap = null;
        reportValid = false;
        message('');
        renderAssignments();
        request('preview');
    }

    function clearDrag() {
        element.querySelectorAll('.lineup-editor-dragging, .lineup-editor-drop-active, .lineup-editor-swap-target').forEach((node) => {
            node.classList.remove('lineup-editor-dragging', 'lineup-editor-drop-active', 'lineup-editor-swap-target');
        });
        dragId = null;
    }

    function targetAt(target) {
        const team = target?.closest?.('[data-lineup-team]');
        if (!team || !element.contains(team)) return null;
        const row = target.closest('[data-lineup-player]');
        return { group: Number(team.dataset.lineupTeam), id: row?.dataset.lineupPlayer || null, team, row };
    }

    function highlight(target) {
        element.querySelectorAll('.lineup-editor-drop-active, .lineup-editor-swap-target').forEach((node) => node.classList.remove('lineup-editor-drop-active', 'lineup-editor-swap-target'));
        const drop = targetAt(target);
        if (!drop || !dragId || assignments[dragId] === drop.group) return;
        (drop.row || drop.team).classList.add(drop.row ? 'lineup-editor-swap-target' : 'lineup-editor-drop-active');
    }

    element.addEventListener('click', (event) => {
        const move = event.target.closest('[data-lineup-move]');
        if (move) {
            if (!move.disabled) change(move.closest('[data-lineup-player]').dataset.lineupPlayer, Number(move.dataset.lineupMove));
            return;
        }
        const main = event.target.closest('[data-lineup-row-main]');
        if (main && !event.target.closest('[data-lineup-handle]')) {
            const row = main.closest('[data-lineup-player]');
            const toggle = row.querySelector('[data-lineup-profile-toggle]');
            const panel = row.querySelector('[data-lineup-profile-panel]');
            const open = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            panel.hidden = !open;
            return;
        }
        if (event.target.closest('[data-lineup-generate]')) request('generate');
        if (event.target.closest('[data-lineup-retry]')) request('preview');
        if (event.target.closest('[data-lineup-swap-teams]') && editable()
            && Object.values(assignments).some((group) => group === 1 || group === 2)) {
            stopPointerDrag();
            remember();
            Object.keys(assignments).forEach((id) => {
                if (assignments[id] === 1 || assignments[id] === 2) assignments[id] = 3 - assignments[id];
            });
            selectedSwap = null;
            reportValid = false;
            message('');
            renderAssignments();
            request('preview');
        }
        if (event.target.closest('[data-lineup-undo]') && editable() && undo.length) {
            assignments = undo.pop();
            selectedSwap = null;
            reportValid = false;
            message('');
            renderAssignments();
            request('preview');
        }
        const swap = event.target.closest('[data-lineup-swap]');
        if (swap && editable()) {
            const id = swap.closest('[data-lineup-player]').dataset.lineupPlayer;
            if (selectedSwap === id) selectedSwap = null;
            else if (selectedSwap && assignments[selectedSwap] !== assignments[id]) change(selectedSwap, assignments[id], id);
            else selectedSwap = id;
            controls();
        }
    });
    form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!find('[data-lineup-save]').disabled) request('save');
    });
    element.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            selectedSwap = null;
            stopPointerDrag();
            controls();
        }
    });

    // Use one pointer-based gesture for mouse, touch and pen. The handle is a
    // button, so do not rely on the browser starting an HTML5 drag from it.
    function stopPointerDrag() {
        const previous = pointerDrag;
        pointerDrag = null;
        if (dragFrame !== null) window.cancelAnimationFrame(dragFrame);
        dragFrame = null;
        dragPreview?.remove();
        dragPreview = null;
        clearDrag();
        if (previous?.handle.hasPointerCapture?.(previous.pointerId)) {
            previous.handle.releasePointerCapture(previous.pointerId);
        }
    }

    function paintPointerDrag() {
        if (!pointerDrag?.active || !dragPreview) return;
        const { x, y } = pointerDrag;
        const target = document.elementFromPoint(x, y);
        const drop = targetAt(target);
        highlight(target);
        const player = byId.get(dragId);
        const valid = drop && assignments[dragId] !== drop.group;
        const destination = valid
            ? (drop.id ? `Vyměnit za ${byId.get(drop.id).nickname}` : (drop.group ? `Přesunout do týmu ${drop.group}` : 'Přesunout mezi nezařazené'))
            : 'Přesuň nad druhý tým nebo nezařazené';
        dragPreview.textContent = `${player.nickname} · ${destination}`;
        dragPreview.style.left = `${Math.max(8, Math.min(x + 16, window.innerWidth - Math.min(300, window.innerWidth - 16) - 8))}px`;
        dragPreview.style.top = `${Math.max(8, Math.min(y + 18, window.innerHeight - dragPreview.offsetHeight - 8))}px`;
    }

    function scrollPointerDrag() {
        if (!pointerDrag?.active || !element.isConnected) {
            stopPointerDrag();
            return;
        }
        const edge = 72;
        const y = pointerDrag.y;
        let step = 0;
        if (y < edge) step = -Math.ceil((edge - y) / 4);
        else if (y > window.innerHeight - edge) step = Math.ceil((y - window.innerHeight + edge) / 4);
        if (step) window.scrollBy(0, Math.max(-18, Math.min(18, step)));
        paintPointerDrag();
        dragFrame = window.requestAnimationFrame(scrollPointerDrag);
    }

    element.addEventListener('dragstart', (event) => {
        if (event.target.closest('[data-lineup-player]')) event.preventDefault();
    });
    element.addEventListener('pointerdown', (event) => {
        const handle = event.target.closest('[data-lineup-handle]');
        if (!handle || !editable() || event.isPrimary === false || (event.button != null && event.button !== 0)) return;
        stopPointerDrag();
        event.preventDefault();
        handle.focus({ preventScroll: true });
        pointerDrag = {
            pointerId: event.pointerId, handle, active: false,
            startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY,
        };
        dragId = handle.closest('[data-lineup-player]').dataset.lineupPlayer;
        try {
            handle.setPointerCapture(event.pointerId);
        } catch {
            stopPointerDrag();
        }
    });
    element.addEventListener('pointermove', (event) => {
        if (!pointerDrag || pointerDrag.pointerId !== event.pointerId) return;
        event.preventDefault();
        pointerDrag.x = event.clientX;
        pointerDrag.y = event.clientY;
        if (!pointerDrag.active) {
            if (Math.hypot(event.clientX - pointerDrag.startX, event.clientY - pointerDrag.startY) < 5) return;
            pointerDrag.active = true;
            rows.get(dragId).classList.add('lineup-editor-dragging');
            dragPreview = document.createElement('div');
            dragPreview.className = 'lineup-editor-drag-preview';
            dragPreview.setAttribute('aria-hidden', 'true');
            document.body.append(dragPreview);
            dragFrame = window.requestAnimationFrame(scrollPointerDrag);
        }
        paintPointerDrag();
    });
    element.addEventListener('pointerup', (event) => {
        if (!pointerDrag || pointerDrag.pointerId !== event.pointerId) return;
        event.preventDefault();
        const drop = pointerDrag.active ? targetAt(document.elementFromPoint(event.clientX, event.clientY)) : null;
        const id = dragId;
        stopPointerDrag();
        if (drop) change(id, drop.group, drop.id);
    });
    element.addEventListener('pointercancel', (event) => {
        if (pointerDrag?.pointerId === event.pointerId) stopPointerDrag();
    });
    element.addEventListener('lostpointercapture', (event) => {
        if (pointerDrag?.pointerId === event.pointerId) stopPointerDrag();
    });

    buildRows();
    renderAssignments();
    renderReport();
    renderRules();
    return { element, dirty, cancelDrag: stopPointerDrag };
}
